import { FC, useState, useMemo, useCallback, useEffect } from 'react';

import { styled } from '@mui/material';
import { ZIndexes } from 'enums/z-indexes';
import { useTranslation } from 'react-i18next';
import { CenteredModal } from 'components/styled/centered-modal';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import ReportPhotoDrawer from './report-drawer';
import PreviewHeader from './preview-header';
import LockedLockIcon from 'assets/icons/locked-lock.icon.png';
import AppBackdrop from 'components/app-backdrop';
import { colors } from 'constants/colors';

const StyledControlls = styled('div')({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
});

const StyledImage = styled('img')(({ theme }) => ({
  maxWidth: '98vw',
  maxHeight: '95dvh',
  [theme.breakpoints.up(1400)]: {
    maxWidth: '1400px',
  },
}));

const StyledVideo = styled('video')(({ theme }) => ({
  maxWidth: '98vw',
  maxHeight: '95dvh',
  [theme.breakpoints.up(1400)]: {
    maxWidth: '1400px',
  },
}));

interface PreviewAssetsProps {
  opened: boolean;
  assets: any[] | null;
  index?: number;
  isPublic?: boolean;
  hasControlls?: boolean;
  onClose: () => any;
  isSubscribed?: boolean;
  handleOpenSubscribe?: () => void;
}

const PreviewAssets: FC<PreviewAssetsProps> = ({
  opened,
  index,
  assets,
  hasControlls = true,
  onClose,
  isSubscribed,
  handleOpenSubscribe,
  isPublic = false,
}) => {
  const { t } = useTranslation();

  const [report, setReport] = useState(false);

  const content: any[] | null = useMemo(
    () =>
      assets?.reduce((result, next) => {
        result.push(...next);
        return result;
      }, []),
    [assets]
  );
  const hasContent = useMemo(() => content?.length, [content]);
  const contentAsset = content && index !== undefined ? content[index] : null;
  const [contentIndex, setContentIndex] = useState<number | null>(null);
  const [prevImage, setPrevImage] = useState<any>(null);
  const [nextImage, setNextImage] = useState<any>(null);
  const [loaded, setLoaded] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    onClose();
    setContentIndex(null);
    setLoaded(false);
  }, [onClose]);

  const handleCloseReport = () => setReport(false);
  const handleOpenReport = () => {
    if (isPublic) {
      return;
    }
    setReport(true);
  };

  const handleNextContent = useCallback(async () => {
    if (!content || contentIndex === null) {
      return;
    }
    setLoaded(false);
    await Promise.all([
      setPrevImage(content[contentIndex]),
      setContentIndex((index) => {
        if (index === null) {
          return null;
        }
        return index + 1 === content.length ? 0 : index + 1;
      }),
      setNextImage(content[(contentIndex + 2) % content.length]),
    ]);
  }, [content, contentIndex]);

  const handlePreviousContent = useCallback(async () => {
    if (!content || contentIndex === null) {
      return;
    }
    setLoaded(false);
    await Promise.all([
      setNextImage(content[contentIndex]),
      setContentIndex((index) => {
        if (index === null) {
          return null;
        }
        return index - 1 === -1 ? content.length - 1 : index - 1;
      }),
      setPrevImage(
        content[(contentIndex - 2 + content.length) % content.length]
      ),
    ]);
  }, [content, contentIndex]);

  useEffect(() => {
    if (!content || !opened) {
      return;
    }

    if (index !== undefined) {
      setContentIndex(index);
      setPrevImage(content[(index - 2 + content.length) % content.length]);
      setNextImage(content[(index + 2) % content.length]);
      return;
    }

    setContentIndex(0);
  }, [content, index, opened]);

  useEffect(() => {
    const handleEscapeKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKeyPress);

    return () => {
      document.removeEventListener('keydown', handleEscapeKeyPress);
    };
  }, [handleClose]);

  useEffect(() => {
    if (!contentIndex || !handleOpenSubscribe) {
      return;
    }
    if (contentIndex > 2 && !isSubscribed) {
      handleClose();
      handleOpenSubscribe();
    }
  }, [contentIndex, handleOpenSubscribe, isSubscribed, handleClose]);

  if (!assets) {
    return <></>;
  }

  if (contentIndex === null) {
    return <></>;
  }
  return (
    <CenteredModal
      open={opened}
      sx={{
        background: { md: 'rgba(0, 0, 0, .6)', xs: '#272727' },
        zIndex: ZIndexes.External_Modal_First,
      }}
    >
      <>
        <PreviewHeader onClose={handleClose} onReport={handleOpenReport} />
        {!loaded && (
          <AppBackdrop
            styles={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
            }}
          />
        )}
        {hasControlls && hasContent && (
          <StyledControlls sx={{ left: '20px' }}>
            <IconButton color="info" onClick={handlePreviousContent}>
              <ArrowBackIcon />
            </IconButton>
          </StyledControlls>
        )}
        {prevImage && content && (
          <img
            alt="preview"
            src={prevImage.content}
            style={{ display: 'none' }}
          />
        )}

        {hasContent && content && content[contentIndex].type === 'image' && (
          <StyledImage
            alt="selected preview"
            src={content[contentIndex].content}
            loading="lazy"
            onClick={handleNextContent}
            // sx={{ maxWidth: { xs: '98vw', '1400': '1400px' } }}
            onLoad={() => setLoaded(true)}
          />
        )}

        {nextImage && content && (
          <img
            alt="preview"
            src={nextImage.content}
            style={{ display: 'none' }}
          />
        )}
        {hasContent && content && content[contentIndex].type === 'video' && (
          <StyledVideo
            src={content[contentIndex].content}
            controls
            autoPlay
            muted
            onLoadStart={() => {
              setLoaded(true);
            }}
          />
        )}
        {hasControlls && hasContent && (
          <StyledControlls sx={{ right: '20px' }}>
            <IconButton color="info" onClick={handleNextContent}>
              <ArrowForwardIcon />
            </IconButton>
          </StyledControlls>
        )}
        {!isSubscribed && (
          <>
            <Button
              size="small"
              sx={{
                position: 'absolute',
                right: 10,
                bottom: 70,
                display: { xs: 'block', sm: 'none' },
                padding: 0,
                '& .MuiButton-startIcon': {
                  marginRight: 0,
                },
                '& svg': {
                  overflow: 'initial',
                  '& circle': {
                    stroke: '#FFF',
                    strokeWidth: 2,
                  },
                },
              }}
              startIcon={
                <img
                  src={LockedLockIcon}
                  style={{
                    width: 47,
                    height: 47,
                    borderRadius: '50%',
                    border: '2px solid #fff',
                  }}
                />
              }
              onClick={handleOpenSubscribe}
            >
              <Typography
                sx={{
                  color: colors.default.info,
                  fontSize: '11px',
                  lineHeight: '15px',
                  textTransform: 'uppercase',
                  marginTop: '4px',
                }}
              >
                subscribe
              </Typography>
            </Button>
            <Grid
              item
              sx={{
                width: 378,
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                padding: '14px 23px',
                background: '#343434',
                borderRadius: '14px',
                position: 'absolute',
                top: 64,
                right: 80,
              }}
            >
              <Typography
                variant="body2"
                component="p"
                color="dark-white"
                textTransform="uppercase"
                sx={{ fontFamily: 'Avenir', lineHeight: '27px' }}
              >
                {t('titles.view-my-camera-roll')}:
              </Typography>
              <Button
                variant="contained"
                color="green"
                size="small"
                startIcon={
                  <img
                    src={LockedLockIcon}
                    style={{ width: 45, height: 45, marginRight: 4 }}
                  />
                }
                onClick={handleOpenSubscribe}
                sx={{
                  marginTop: '15px',
                  justifyContent: 'flex-start',
                  padding: '6px',
                  lineHeight: '22px',
                }}
              >
                {t('buttons.unlock-roll')}
              </Button>
            </Grid>
          </>
        )}

        <ReportPhotoDrawer
          open={report}
          asset={contentAsset}
          onClose={handleCloseReport}
        />
      </>
    </CenteredModal>
  );
};

export default PreviewAssets;
